import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import type { PersonVote } from "../../api/votes/votes";
import { PitchDraftDiff } from "./pitch-draft-diff";

type DebugEntry = {
	type: string;
	date: string;
	data: unknown;
	[key: string]: unknown;
};

interface ActivityDebugDialogProps {
	open: boolean;
	onClose: () => void;
	entry: DebugEntry | null;
	/** Staff and superusers see the approved-pitch diff on vote entries. */
	isStaff?: boolean;
}

export const ActivityDebugDialog = ({
	open,
	onClose,
	entry,
	isStaff = false,
}: ActivityDebugDialogProps) => {
	if (!entry) return null;

	const vote = isStaff && entry.type === "vote" ? (entry.data as PersonVote) : null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			data-component="activity-debug-dialog"
		>
			<DialogTitle>Debug: {entry.type}</DialogTitle>
			<DialogContent>
				{vote && <PitchDraftDiff vote={vote} />}
				<Box sx={{ mt: 2 }}>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Entry Metadata
					</Typography>
					<Box
						sx={{
							p: 1,
							mb: 2,
							bgcolor: "action.hover",
							borderRadius: 1,
							fontFamily: "monospace",
							fontSize: 12,
						}}
					>
						<div>
							<strong>type:</strong> {entry.type}
						</div>
						<div>
							<strong>date:</strong> {entry.date}
						</div>
						{Object.entries(entry)
							.filter(([key]) => !["type", "date", "data"].includes(key))
							.map(([key, value]) => (
								<div key={key}>
									<strong>{key}:</strong> {JSON.stringify(value)}
								</div>
							))}
					</Box>

					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Raw Data
					</Typography>
					<Box
						sx={{
							p: 1,
							border: 1,
							borderColor: "divider",
							borderRadius: 1,
							maxHeight: 500,
							overflow: "auto",
							fontFamily: "monospace",
							fontSize: 12,
							whiteSpace: "pre-wrap",
							wordBreak: "break-all",
						}}
					>
						{JSON.stringify(entry.data, null, 2)}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};
